import { WorkspacesAPITagType } from '@/api/api'
import { userApi } from '@/api/user-api'
import getWorkspaceApiUrl from '@/api/utils/get-workspace-api-url'
import { workspacesApi } from '@/api/workspaces-api'
import {
  makePredictiveReportCandidateBasicInfoListDemo,
  makePredictiveReportCandidateListAllDemo,
  makePredictiveReportCandidateListCustomDemo,
  makePredictiveReportCandidateListFacebookDemo,
  makePredictiveReportCandidateListInstagramDemo,
  makePredictiveReportCandidateListYoutubeDemo,
  makePredictiveReportDetailReportKolsFacebookDemo,
  makePredictiveReportDetailReportKolsInstagramDemo,
  makePredictiveReportDetailReportKolsYoutubeDemo,
  makePredictiveReportDetailReportSubtotalDemo,
  predictiveReportConfigDemo,
  predictiveReportDemo,
  predictiveReportDetailReportKolsCustomDemo,
  predictiveReportDetailReportPlatformSubtotalCustomDemo,
  predictiveReportDetailReportPlatformSubtotalFacebookDemo,
  predictiveReportDetailReportPlatformSubtotalInstagramDemo,
  predictiveReportDetailReportPlatformSubtotalYoutubeDemo,
  predictiveReportSubtotalDemo,
} from '@/components/predictive-report/constants/demo/api'
import { RootState } from '@/store/store'
import {
  AddPredictiveReportCandidateListRequest,
  AddPredictiveReportCandidateListResponse,
  addPredictiveReportCandidateListResponseSchema,
  CreatePredictiveReportRequest,
  CreatePredictiveReportResponse,
  createPredictiveReportResponseSchema,
  DuplicatePredictiveReportRequest,
  DuplicatePredictiveReportResponse,
  duplicatePredictiveReportResponseSchema,
  EditPredictiveReportCandidateAdvertorialContentListRequest,
  EditPredictiveReportCandidateAdvertorialContentListResponse,
  editPredictiveReportCandidateAdvertorialContentListResponseSchema,
  EditPredictiveReportCandidateCollaborativeItemsRequest,
  EditPredictiveReportCandidateCollaborativeItemsResponse,
  editPredictiveReportCandidateCollaborativeItemsResponseSchema,
  EditPredictiveReportCandidateCustomDataRequest,
  EditPredictiveReportCandidateCustomDataResponse,
  editPredictiveReportCandidateCustomDataResponseSchema,
  EditPredictiveReportCandidateNoteRequest,
  EditPredictiveReportCandidateNoteResponse,
  editPredictiveReportCandidateNoteResponseSchema,
  EditPredictiveReportCandidateSelectStatusRequest,
  EditPredictiveReportCandidateSelectStatusResponse,
  editPredictiveReportCandidateSelectStatusResponseSchema,
  EditPredictiveReportCandidateWillingnessToCooperateRequest,
  EditPredictiveReportCandidateWillingnessToCooperateResponse,
  editPredictiveReportCandidateWillingnessToCooperateResponseSchema,
  EditPredictiveReportCollaborativeItemsRequest,
  EditPredictiveReportCollaborativeItemsResponse,
  editPredictiveReportCollaborativeItemsResponseSchema,
  EditPredictiveReportRequest,
  EditPredictiveReportResponse,
  editPredictiveReportResponseSchema,
  ExportPredictiveReportXlsxRequest,
  ExportPredictiveReportXlsxResponse,
  FetchPredictiveReportCollaborativeItemEstimatedPriceRequest,
  FetchPredictiveReportCollaborativeItemEstimatedPriceResponse,
  fetchPredictiveReportCollaborativeItemEstimatedPriceResponseSchema,
  fetchPredictiveReportKolInvolvesResponseSchema,
  PredictiveReportCandidateBasicInfoListRequest,
  PredictiveReportCandidateBasicInfoListResponse,
  predictiveReportCandidateBasicInfoResponseSchema,
  PredictiveReportCandidateListRequest,
  PredictiveReportCandidateListResponse,
  predictiveReportCandidateListResponseSchema,
  PredictiveReportCollaborativeItemsRequest,
  PredictiveReportCollaborativeItemsResponse,
  predictiveReportCollaborativeItemsResponseSchema,
  PredictiveReportConfigRequest,
  PredictiveReportConfigResponse,
  predictiveReportConfigResponseSchema,
  PredictiveReportDetailReportKolsRequest,
  PredictiveReportDetailReportKolsResponse,
  predictiveReportDetailReportKolsResponseSchema,
  PredictiveReportDetailReportPlatformSubtotalRequest,
  PredictiveReportDetailReportPlatformSubtotalResponse,
  predictiveReportDetailReportPlatformSubtotalResponseSchema,
  PredictiveReportDetailReportSubtotalRequest,
  PredictiveReportDetailReportSubtotalResponse,
  predictiveReportDetailReportSubtotalResponseSchema,
  PredictiveReportListRequest,
  PredictiveReportListResponse,
  predictiveReportListResponseSchema,
  PredictiveReportSubtotalRequest,
  PredictiveReportSubtotalResponse,
  predictiveReportSubtotalResponseSchema,
  RefreshPredictiveReportDetailReportSummaryRequest,
  RefreshPredictiveReportDetailReportSummaryResponse,
  refreshPredictiveReportDetailReportSummaryResponseSchema,
  RemovePredictiveReportCandidatePlatformRequest,
  RemovePredictiveReportCandidatePlatformResponse,
  removePredictiveReportCandidatePlatformResponseSchema,
  RemovePredictiveReportRequest,
  RemovePredictiveReportResponse,
  removePredictiveReportResponseSchema,
  type FetchPredictiveReportKolInvolvesRequest,
  type FetchPredictiveReportKolInvolvesResponse,
} from '@/types/schema/api/predictive-report'
import { PlatformShortcode } from '@/utils/convert-platform'
import { toSupportedLanguage } from '@/utils/i18n/to-supported-language'
import removeNil from '@/utils/remove-nil'

const predictiveReportApi = workspacesApi.injectEndpoints({
  endpoints: (builder) => ({
    // 效益預測報告 列表
    fetchPredictiveReportList: builder.query<
      PredictiveReportListResponse,
      PredictiveReportListRequest
    >({
      query: (params) => {
        return {
          url: getWorkspaceApiUrl('/predictive-reports', 'v3'),
          params: removeNil(params),
        }
      },
      extraOptions: {
        dataSchema: predictiveReportListResponseSchema,
      },
      providesTags: [
        WorkspacesAPITagType.DependPredictiveReport,
        WorkspacesAPITagType.DependPredictiveReportCandidate,
      ],
    }),
    fetchPredictiveReportListInfinite: builder.infiniteQuery<
      PredictiveReportListResponse,
      Omit<PredictiveReportListRequest, 'page'>,
      PredictiveReportListRequest['page']
    >({
      query: ({ queryArg, pageParam }) => {
        const params = { ...queryArg, page: pageParam }
        return {
          url: getWorkspaceApiUrl('/predictive-reports', 'v3'),
          params: removeNil(params),
        }
      },
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.total_page <= lastPageParam) {
            return null
          }

          return lastPageParam + 1
        },
      },
      extraOptions: {
        dataSchema: predictiveReportListResponseSchema,
      },
      providesTags: [
        WorkspacesAPITagType.DependPredictiveReport,
        WorkspacesAPITagType.DependPredictiveReportCandidate,
      ],
    }),
    // 取得 效益預測報告 設置
    fetchPredictiveReportConfig: builder.query<
      PredictiveReportConfigResponse,
      PredictiveReportConfigRequest
    >({
      queryFn: async (id, _queryApi, _extraOptions, baseQuery) => {
        if (id === predictiveReportDemo.id) {
          return { data: predictiveReportConfigDemo }
        }
        return (await baseQuery({
          url: getWorkspaceApiUrl(`/predictive-reports/${id}`, 'v3'),
        })) as { data: PredictiveReportConfigResponse }
      },
      extraOptions: {
        dataSchema: predictiveReportConfigResponseSchema,
      },
      providesTags: [
        WorkspacesAPITagType.DependPredictiveReport,
        WorkspacesAPITagType.DependPredictiveReportCandidate,
        WorkspacesAPITagType.DependPredictiveReportAiSummary,
      ],
    }),
    // 取得 效益預測報告 網紅人選 小計(全平台+各平台)
    fetchPredictiveReportSubtotal: builder.query<
      PredictiveReportSubtotalResponse,
      PredictiveReportSubtotalRequest
    >({
      queryFn: async (id, _queryApi, _extraOptions, baseQuery) => {
        if (id === predictiveReportDemo.id) {
          return { data: predictiveReportSubtotalDemo }
        }
        return (await baseQuery({
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/kols/subtotal`,
            'v3',
          ),
        })) as { data: PredictiveReportSubtotalResponse }
      },
      extraOptions: {
        dataSchema: predictiveReportSubtotalResponseSchema,
      },
      providesTags: [
        WorkspacesAPITagType.DependPredictiveReport,
        WorkspacesAPITagType.DependPredictiveReportCandidate,
      ],
    }),
    // 取得 效益預測報告 網紅人選列表 by 平台
    fetchPredictiveReportCandidateList: builder.query<
      PredictiveReportCandidateListResponse,
      PredictiveReportCandidateListRequest
    >({
      queryFn: async (
        { id, ...params },
        queryApi,
        _extraOptions,
        baseQuery,
      ) => {
        if (id === predictiveReportDemo.id) {
          const state = queryApi.getState() as RootState
          const { data: userInfo } =
            userApi.endpoints.fetchUserInfo.select()(state)
          const language = toSupportedLanguage(userInfo?.language)

          if (params.platform === PlatformShortcode.Instagram) {
            return {
              data: makePredictiveReportCandidateListInstagramDemo(language),
            }
          }
          if (params.platform === PlatformShortcode.Facebook) {
            return {
              data: makePredictiveReportCandidateListFacebookDemo(language),
            }
          }
          if (params.platform === PlatformShortcode.YouTube) {
            return {
              data: makePredictiveReportCandidateListYoutubeDemo(language),
            }
          }
          if (params.platform === 'custom') {
            return {
              data: makePredictiveReportCandidateListCustomDemo(language),
            }
          }
          return {
            data: makePredictiveReportCandidateListAllDemo(language),
          }
        }

        return (await baseQuery({
          url: getWorkspaceApiUrl(`/predictive-reports/${id}/kols`, 'v3'),
          params: removeNil(params),
        })) as { data: PredictiveReportCandidateListResponse }
      },
      extraOptions: {
        dataSchema: predictiveReportCandidateListResponseSchema,
      },
      providesTags: [
        WorkspacesAPITagType.DependPredictiveReportCandidate,
        WorkspacesAPITagType.Demo,
      ],
    }),
    // 效益預測報告有哪些 kol (uuid+name)
    fetchPredictiveReportCandidateBasicInfoList: builder.query<
      PredictiveReportCandidateBasicInfoListResponse,
      PredictiveReportCandidateBasicInfoListRequest
    >({
      queryFn: async (
        { id, ...params },
        queryApi,
        _extraOptions,
        baseQuery,
      ) => {
        if (id === predictiveReportDemo.id) {
          const state = queryApi.getState() as RootState
          const { data: userInfo } =
            userApi.endpoints.fetchUserInfo.select()(state)
          const language = toSupportedLanguage(userInfo?.language)

          return {
            data: makePredictiveReportCandidateBasicInfoListDemo(language),
          }
        }
        return (await baseQuery({
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/kols-basic-info`,
            'v3',
          ),
          params: removeNil(params),
        })) as { data: PredictiveReportCandidateBasicInfoListResponse }
      },
      extraOptions: {
        dataSchema: predictiveReportCandidateBasicInfoResponseSchema,
      },
      providesTags: [
        WorkspacesAPITagType.DependPredictiveReportCandidate,
        WorkspacesAPITagType.Demo,
      ],
    }),
    // 建立 效益預測報告
    createPredictiveReport: builder.mutation<
      CreatePredictiveReportResponse,
      CreatePredictiveReportRequest
    >({
      query: (body /* params, _queryApi, _extraOptions, baseQuery */) => {
        return {
          url: getWorkspaceApiUrl('/predictive-reports', 'v3'),
          method: 'POST',
          body,
        }
      },
      extraOptions: {
        dataSchema: createPredictiveReportResponseSchema,
      },
      invalidatesTags: [WorkspacesAPITagType.DependPredictiveReport],
    }),
    // 編輯 效益預測報告
    editPredictiveReport: builder.mutation<
      EditPredictiveReportResponse,
      EditPredictiveReportRequest
    >({
      query: ({ id, ...body }) => {
        return {
          url: getWorkspaceApiUrl(`/predictive-reports/${id}`, 'v3'),
          method: 'PATCH',
          body,
        }
      },
      extraOptions: {
        dataSchema: editPredictiveReportResponseSchema,
      },
      invalidatesTags: [WorkspacesAPITagType.DependPredictiveReport],
    }),
    // 複製 效益預測報告
    duplicatePredictiveReport: builder.mutation<
      DuplicatePredictiveReportResponse,
      DuplicatePredictiveReportRequest
    >({
      query: ({ id, ...body }) => {
        return {
          url: getWorkspaceApiUrl(`/predictive-reports/${id}/duplicate`, 'v3'),
          method: 'POST',
          body,
        }
      },
      extraOptions: {
        dataSchema: duplicatePredictiveReportResponseSchema,
      },
      invalidatesTags: [WorkspacesAPITagType.DependPredictiveReport],
    }),
    // 刪除 效益預測報告
    removePredictiveReport: builder.mutation<
      RemovePredictiveReportResponse,
      RemovePredictiveReportRequest
    >({
      query: (id) => {
        return {
          url: getWorkspaceApiUrl(`/predictive-reports/${id}`, 'v3'),
          method: 'DELETE',
        }
      },
      extraOptions: {
        dataSchema: removePredictiveReportResponseSchema,
      },
      invalidatesTags: [WorkspacesAPITagType.DependPredictiveReport],
    }),
    // 加入 KOLs 到 效益預測報告中
    addPredictiveReportCandidateList: builder.mutation<
      AddPredictiveReportCandidateListResponse,
      AddPredictiveReportCandidateListRequest
    >({
      query: ({ id, ...body }) => {
        return {
          url: getWorkspaceApiUrl(`/predictive-reports/${id}/kols`, 'v3'),
          method: 'POST',
          body,
        }
      },
      extraOptions: {
        dataSchema: addPredictiveReportCandidateListResponseSchema,
      },
      invalidatesTags: [
        WorkspacesAPITagType.DependPredictiveReportCandidate,
        WorkspacesAPITagType.PredictiveReportKolInvolves,
      ],
    }),
    // 編輯 效益預測報告 某平台中的 單一 kol 選擇狀態
    editPredictiveReportCandidateSelectStatus: builder.mutation<
      EditPredictiveReportCandidateSelectStatusResponse,
      EditPredictiveReportCandidateSelectStatusRequest
    >({
      query: ({ id, predictive_report_kol_id, ...body }) => {
        return {
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/predictive-report-kol/${predictive_report_kol_id}/select`,
            'v3',
          ),
          method: 'PATCH',
          body,
        }
      },
      extraOptions: {
        dataSchema: editPredictiveReportCandidateSelectStatusResponseSchema,
      },
      invalidatesTags: [WorkspacesAPITagType.DependPredictiveReportCandidate],
    }),
    // 編輯 效益預測報告 某平台中的 單一 kol 備註
    editPredictiveReportCandidateNote: builder.mutation<
      EditPredictiveReportCandidateNoteResponse,
      EditPredictiveReportCandidateNoteRequest
    >({
      query: ({ id, predictive_report_kol_id, ...body }) => {
        return {
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/predictive-report-kol/${predictive_report_kol_id}/note`,
            'v3',
          ),
          method: 'PATCH',
          body,
        }
      },
      extraOptions: {
        dataSchema: editPredictiveReportCandidateNoteResponseSchema,
      },
      invalidatesTags: [WorkspacesAPITagType.DependPredictiveReportCandidate],
    }),
    // 編輯 效益預測報告 某平台中的 單一 kol 合作意願
    editPredictiveReportCandidateWillingnessToCooperate: builder.mutation<
      EditPredictiveReportCandidateWillingnessToCooperateResponse,
      EditPredictiveReportCandidateWillingnessToCooperateRequest
    >({
      query: ({ id, predictive_report_kol_id, ...body }) => {
        return {
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/predictive-report-kol/${predictive_report_kol_id}/willingness-to-collaborate`,
            'v3',
          ),
          method: 'PATCH',
          body,
        }
      },
      extraOptions: {
        dataSchema:
          editPredictiveReportCandidateWillingnessToCooperateResponseSchema,
      },
      invalidatesTags: [
        WorkspacesAPITagType.DependPredictiveReportCandidate,
        WorkspacesAPITagType.DirectoryKolFootprint,
      ],
    }),
    //  編輯 效益預測報告中的 kol(單一) 合作項目
    editPredictiveReportCandidateCollaborativeItems: builder.mutation<
      EditPredictiveReportCandidateCollaborativeItemsResponse,
      EditPredictiveReportCandidateCollaborativeItemsRequest
    >({
      query: ({ id, uuid, ...body }) => {
        return {
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/kols/${uuid}/collaborative-items`,
            'v3',
          ),
          method: 'PATCH',
          body,
        }
      },
      extraOptions: {
        dataSchema:
          editPredictiveReportCandidateCollaborativeItemsResponseSchema,
      },
      invalidatesTags: [
        WorkspacesAPITagType.DependPredictiveReportCandidate,
        WorkspacesAPITagType.PredictiveReportQuotation,
      ],
    }),
    // 編輯 效益預測報告 某平台中 單一 kol 的貼文
    editPredictiveReportCandidateAdvertorialContentList: builder.mutation<
      EditPredictiveReportCandidateAdvertorialContentListResponse,
      EditPredictiveReportCandidateAdvertorialContentListRequest
    >({
      query: ({ id, predictive_report_kol_id, ...body }) => {
        return {
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/predictive-report-kol/${predictive_report_kol_id}/posts`,
            'v3',
          ),
          method: 'PATCH',
          body,
        }
      },
      extraOptions: {
        dataSchema:
          editPredictiveReportCandidateAdvertorialContentListResponseSchema,
      },
      invalidatesTags: [WorkspacesAPITagType.DependPredictiveReportCandidate],
    }),
    // 刪除 效益預測報告 中某平台的 kol
    removePredictiveReportCandidatePlatform: builder.mutation<
      RemovePredictiveReportCandidatePlatformResponse,
      RemovePredictiveReportCandidatePlatformRequest
    >({
      query: ({ id, predictive_report_kol_id }) => {
        return {
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/predictive-report-kol/${predictive_report_kol_id}`,
            'v3',
          ),
          method: 'DELETE',
        }
      },
      extraOptions: {
        dataSchema: removePredictiveReportCandidatePlatformResponseSchema,
      },
      invalidatesTags: [WorkspacesAPITagType.DependPredictiveReportCandidate],
    }),
    // 取得 效益預測報告 合作項目 (單一/已選擇/已勾選的自訂欄位)
    fetchPredictiveReportCollaborativeItems: builder.query<
      PredictiveReportCollaborativeItemsResponse,
      PredictiveReportCollaborativeItemsRequest
    >({
      query: ({ id, ...params }) => {
        return {
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/collaborative-items`,
            'v3',
          ),
          params: removeNil(params),
        }
      },
      extraOptions: {
        dataSchema: predictiveReportCollaborativeItemsResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.DependPredictiveReportCandidate],
    }),
    // 取得 效益預測報告 的報告 整體小計
    fetchPredictiveReportDetailReportSubtotal: builder.query<
      PredictiveReportDetailReportSubtotalResponse,
      PredictiveReportDetailReportSubtotalRequest
    >({
      queryFn: async (id, queryApi, _extraOptions, baseQuery) => {
        if (id === predictiveReportDemo.id) {
          const state = queryApi.getState() as RootState
          const { data: userInfo } =
            userApi.endpoints.fetchUserInfo.select()(state)
          const language = toSupportedLanguage(userInfo?.language)

          return {
            data: makePredictiveReportDetailReportSubtotalDemo(language),
          }
        }
        return (await baseQuery({
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/report/subtotal`,
            'v3',
          ),
        })) as { data: PredictiveReportDetailReportSubtotalResponse }
      },
      extraOptions: {
        dataSchema: predictiveReportDetailReportSubtotalResponseSchema,
      },
      providesTags: [
        WorkspacesAPITagType.DependPredictiveReport,
        WorkspacesAPITagType.DependPredictiveReportCandidate,
        WorkspacesAPITagType.DependPredictiveReportAiSummary,
        WorkspacesAPITagType.Demo,
      ],
    }),
    // 取得 效益預測報告 的報告 平台小計
    fetchPredictiveReportDetailReportPlatformSubtotal: builder.query<
      PredictiveReportDetailReportPlatformSubtotalResponse,
      PredictiveReportDetailReportPlatformSubtotalRequest
    >({
      queryFn: async ({ id, ...body }, _queryApi, _extraOptions, baseQuery) => {
        if (id === predictiveReportDemo.id) {
          if (body.platform === PlatformShortcode.Instagram) {
            return {
              data: predictiveReportDetailReportPlatformSubtotalInstagramDemo,
            }
          }
          if (body.platform === PlatformShortcode.Facebook) {
            return {
              data: predictiveReportDetailReportPlatformSubtotalFacebookDemo,
            }
          }
          if (body.platform === PlatformShortcode.YouTube) {
            return {
              data: predictiveReportDetailReportPlatformSubtotalYoutubeDemo,
            }
          }
          if (body.platform === 'custom') {
            return {
              data: predictiveReportDetailReportPlatformSubtotalCustomDemo,
            }
          }
        }
        return (await baseQuery({
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/report/platform-subtotal`,
            'v3',
          ),
          method: 'POST',
          body: removeNil(body),
        })) as { data: PredictiveReportDetailReportPlatformSubtotalResponse }
      },
      extraOptions: {
        dataSchema: predictiveReportDetailReportPlatformSubtotalResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.DependPredictiveReportCandidate],
    }),
    // 取得 效益預測報告 的報告 平台預估數據
    fetchPredictiveReportDetailReportKols: builder.query<
      PredictiveReportDetailReportKolsResponse,
      PredictiveReportDetailReportKolsRequest
    >({
      queryFn: async ({ id, ...body }, queryApi, _extraOptions, baseQuery) => {
        if (id === predictiveReportDemo.id) {
          const state = queryApi.getState() as RootState
          const { data: userInfo } =
            userApi.endpoints.fetchUserInfo.select()(state)
          const language = toSupportedLanguage(userInfo?.language)

          if (body.platform === PlatformShortcode.Instagram) {
            return {
              data: makePredictiveReportDetailReportKolsInstagramDemo(language),
            }
          }
          if (body.platform === PlatformShortcode.Facebook) {
            return {
              data: makePredictiveReportDetailReportKolsFacebookDemo(language),
            }
          }
          if (body.platform === PlatformShortcode.YouTube) {
            return {
              data: makePredictiveReportDetailReportKolsYoutubeDemo(language),
            }
          }
          if (body.platform === 'custom') {
            return { data: predictiveReportDetailReportKolsCustomDemo }
          }
        }
        return (await baseQuery({
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/report/kols`,
            'v3',
          ),
          method: 'POST',
          body: removeNil(body),
        })) as { data: PredictiveReportDetailReportKolsResponse }
      },
      extraOptions: {
        dataSchema: predictiveReportDetailReportKolsResponseSchema,
      },
      providesTags: [
        WorkspacesAPITagType.DependPredictiveReportCandidate,
        WorkspacesAPITagType.Demo,
      ],
    }),
    // 效益預測報告 excel 匯出
    exportPredictiveReportXlsx: builder.mutation<
      ExportPredictiveReportXlsxResponse,
      ExportPredictiveReportXlsxRequest
    >({
      query: (id) => {
        return {
          url: getWorkspaceApiUrl(`/predictive-reports/${id}/export`, 'v3'),
          method: 'POST',
          responseHandler: (response): Promise<ArrayBuffer> =>
            response.arrayBuffer(),
        }
      },
      invalidatesTags: [],
    }),
    // 編輯 效益預測報告 所有平台的合作內容
    editPredictiveReportCollaborativeItems: builder.mutation<
      EditPredictiveReportCollaborativeItemsResponse,
      EditPredictiveReportCollaborativeItemsRequest
    >({
      query: ({ id, ...body }) => {
        return {
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/report/selected-collaborative-items`,
            'v3',
          ),
          method: 'PATCH',
          body,
        }
      },
      extraOptions: {
        dataSchema: editPredictiveReportCollaborativeItemsResponseSchema,
      },
      invalidatesTags: [WorkspacesAPITagType.DependPredictiveReportCandidate],
    }),
    // 編輯 效益預測報告 中有設定需要自訂數據的 kol 數據 (group by kol)
    editPredictiveReportCandidateCustomData: builder.mutation<
      EditPredictiveReportCandidateCustomDataResponse,
      EditPredictiveReportCandidateCustomDataRequest
    >({
      query: ({ id, ...body }) => {
        return {
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/report/selected-collaborative-items/custom-data`,
            'v3',
          ),
          method: 'PATCH',
          body,
        }
      },
      extraOptions: {
        dataSchema: editPredictiveReportCandidateCustomDataResponseSchema,
      },
      invalidatesTags: [WorkspacesAPITagType.DependPredictiveReportCandidate],
    }),
    // 刷新 效益預測報告的 整體 summary
    refreshPredictiveReportDetailReportSummary: builder.mutation<
      RefreshPredictiveReportDetailReportSummaryResponse,
      RefreshPredictiveReportDetailReportSummaryRequest
    >({
      query: (id) => {
        return {
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/report/summary`,
            'v3',
          ),
          method: 'PATCH',
        }
      },
      extraOptions: {
        dataSchema: refreshPredictiveReportDetailReportSummaryResponseSchema,
      },
      invalidatesTags: [WorkspacesAPITagType.DependPredictiveReportAiSummary],
    }),
    // 取得 AI 預估報價
    fetchPredictiveReportCollaborativeItemEstimatedPrice: builder.query<
      FetchPredictiveReportCollaborativeItemEstimatedPriceResponse,
      FetchPredictiveReportCollaborativeItemEstimatedPriceRequest
    >({
      query: ({ platform, uuid, id }) => {
        return {
          url: getWorkspaceApiUrl(
            `/predictive-reports/${id}/kols/ai_predicted_single_cooperation_fee`,
            'v3',
          ),
          method: 'POST',
          body: {
            uuids: [uuid],
            platform,
          },
        }
      },
      extraOptions: {
        dataSchema:
          fetchPredictiveReportCollaborativeItemEstimatedPriceResponseSchema,
      },
    }),
    fetchPredictiveReportKolInvolves: builder.query<
      FetchPredictiveReportKolInvolvesResponse,
      FetchPredictiveReportKolInvolvesRequest
    >({
      query: ({ kol_uuids }) => {
        return {
          url: getWorkspaceApiUrl(`/predictive-reports/kol-involves`, 'v3'),
          method: 'POST',
          body: {
            kol_uuids,
          },
        }
      },
      extraOptions: {
        dataSchema: fetchPredictiveReportKolInvolvesResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.PredictiveReportKolInvolves],
    }),
  }),
  overrideExisting: false,
})

export const {
  useFetchPredictiveReportListQuery,
  useFetchPredictiveReportConfigQuery,
  useFetchPredictiveReportSubtotalQuery,
  useLazyFetchPredictiveReportSubtotalQuery,
  useFetchPredictiveReportCandidateListQuery,
  useFetchPredictiveReportCandidateBasicInfoListQuery,
  useLazyFetchPredictiveReportCandidateBasicInfoListQuery,
  useFetchPredictiveReportCollaborativeItemsQuery,
  useFetchPredictiveReportDetailReportSubtotalQuery,
  useLazyFetchPredictiveReportDetailReportSubtotalQuery,
  useFetchPredictiveReportDetailReportPlatformSubtotalQuery,
  useLazyFetchPredictiveReportDetailReportPlatformSubtotalQuery,
  useFetchPredictiveReportDetailReportKolsQuery,
  useLazyFetchPredictiveReportDetailReportKolsQuery,
  useCreatePredictiveReportMutation,
  useEditPredictiveReportMutation,
  useDuplicatePredictiveReportMutation,
  useRemovePredictiveReportMutation,
  useAddPredictiveReportCandidateListMutation,
  useEditPredictiveReportCandidateSelectStatusMutation,
  useEditPredictiveReportCandidateCollaborativeItemsMutation,
  useEditPredictiveReportCandidateNoteMutation,
  useEditPredictiveReportCandidateWillingnessToCooperateMutation,
  useEditPredictiveReportCandidateAdvertorialContentListMutation,
  useRemovePredictiveReportCandidatePlatformMutation,
  useExportPredictiveReportXlsxMutation,
  useEditPredictiveReportCollaborativeItemsMutation,
  useEditPredictiveReportCandidateCustomDataMutation,
  useRefreshPredictiveReportDetailReportSummaryMutation,
} = predictiveReportApi
export const useFetchPredictiveReportListInfiniteQuery =
  predictiveReportApi.endpoints.fetchPredictiveReportListInfinite
    .useInfiniteQuery
export const useFetchPredictiveReportKolInvolvesQuery =
  predictiveReportApi.endpoints.fetchPredictiveReportKolInvolves.useQuery
export const useLazyFetchPredictiveReportCollaborativeItemEstimatedPriceQuery =
  predictiveReportApi.endpoints
    .fetchPredictiveReportCollaborativeItemEstimatedPrice.useLazyQuery
